import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, TextField, Button,
  Select, MenuItem, FormControl, InputLabel, Alert, CircularProgress, Divider,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import millingApi from '../../../services/millingApi';

const empty = {
  sampleType: 'grain_lot',
  referenceNumber: '',
  referenceId: '',
  productName: '',
  sampleDate: new Date().toISOString().split('T')[0],
  testedBy: '',
  notes: '',
  // physical
  moisture: '', protein: '', ash: '', fallingNumber: '',
  wetGluten: '', testWeight: '',
  // rheology
  waterAbsorption: '', developmentTime: '', stability: '',
  whitenessIndex: '', granulation: '',
  // safety
  aflatoxin: '', foreignMatter: '', damagedKernels: '',
};

const numField = (
  label: string,
  key: string,
  form: any,
  setForm: any,
  opts?: { step?: number; min?: number; max?: number; helper?: string; error?: boolean }
) => (
  <Grid item xs={12} sm={6} md={4}>
    <TextField
      label={label}
      type="number"
      fullWidth
      size="small"
      value={form[key]}
      onChange={(e) => setForm((f: any) => ({ ...f, [key]: e.target.value }))}
      inputProps={{ step: opts?.step ?? 0.01, min: opts?.min ?? 0, max: opts?.max }}
      helperText={opts?.helper}
      error={opts?.error}
    />
  </Grid>
);

const QualityTestForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;

  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<any>(empty);

  useEffect(() => {
    if (!isEdit) return;
    millingApi.getQualityTest(id!).then((rec) => {
      const f: any = { ...empty };
      Object.keys(empty).forEach((k) => {
        f[k] = rec[k] != null ? String(rec[k]) : '';
      });
      setForm(f);
    }).catch(() => setError('Failed to load test.')).finally(() => setLoading(false));
  }, [id, isEdit]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const numKeys = ['moisture','protein','ash','fallingNumber','wetGluten','testWeight',
      'waterAbsorption','developmentTime','stability','whitenessIndex','aflatoxin','foreignMatter','damagedKernels'];
    const payload: any = { ...form };
    numKeys.forEach((k) => { payload[k] = payload[k] !== '' ? Number(payload[k]) : undefined; });
    if (!payload.referenceId) payload.referenceId = undefined;
    try {
      if (isEdit) {
        await millingApi.updateQualityTest(id!, payload);
      } else {
        await millingApi.createQualityTest(payload);
      }
      navigate('/milling/quality-tests');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

  const aflatoxinVal = Number(form.aflatoxin);

  return (
    <Box sx={{ p: 3, maxWidth: 900 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Button onClick={() => navigate('/milling/quality-tests')}>← Back</Button>
        <Typography variant="h5">{isEdit ? 'Edit Quality Test' : 'New Quality Test'}</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <form onSubmit={handleSubmit}>
        {/* Header */}
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Sample Information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Sample Type *</InputLabel>
                  <Select value={form.sampleType} label="Sample Type *"
                    onChange={(e) => setForm((f: any) => ({ ...f, sampleType: e.target.value }))}>
                    <MenuItem value="grain_lot">Grain Lot</MenuItem>
                    <MenuItem value="tempering">Tempering</MenuItem>
                    <MenuItem value="finished_product">Finished Product</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField label="Reference Number" fullWidth size="small" value={form.referenceNumber}
                  onChange={(e) => setForm((f: any) => ({ ...f, referenceNumber: e.target.value }))}
                  helperText="e.g. lot number or order number" />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField label="Reference ID (UUID)" fullWidth size="small" value={form.referenceId}
                  onChange={(e) => setForm((f: any) => ({ ...f, referenceId: e.target.value }))}
                  helperText="For CoA grouping" />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField label="Product Name" fullWidth size="small" value={form.productName}
                  onChange={(e) => setForm((f: any) => ({ ...f, productName: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField label="Sample Date *" type="date" fullWidth size="small" value={form.sampleDate}
                  onChange={(e) => setForm((f: any) => ({ ...f, sampleDate: e.target.value }))}
                  InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField label="Tested By" fullWidth size="small" value={form.testedBy}
                  onChange={(e) => setForm((f: any) => ({ ...f, testedBy: e.target.value }))} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Physical / Chemical */}
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Physical & Chemical Tests</Typography>
            <Grid container spacing={2}>
              {numField('Moisture (%)', 'moisture', form, setForm, { max: 30 })}
              {numField('Protein (%)', 'protein', form, setForm, { max: 30 })}
              {numField('Ash (%)', 'ash', form, setForm, { step: 0.001, max: 5 })}
              {numField('Falling Number (s)', 'fallingNumber', form, setForm, { step: 1, max: 600 })}
              {numField('Wet Gluten (%)', 'wetGluten', form, setForm, { max: 50 })}
              {numField('Test Weight (kg/hl)', 'testWeight', form, setForm, { max: 100 })}
            </Grid>
          </CardContent>
        </Card>

        {/* Rheology — finished product */}
        {form.sampleType === 'finished_product' && (
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Flour Rheology (Farinograph)</Typography>
              <Grid container spacing={2}>
                {numField('Water Absorption (%)', 'waterAbsorption', form, setForm, { max: 80 })}
                {numField('Development Time (min)', 'developmentTime', form, setForm, { max: 30 })}
                {numField('Stability (min)', 'stability', form, setForm, { max: 30 })}
                {numField('Whiteness Index', 'whitenessIndex', form, setForm, { max: 100 })}
                <Grid item xs={12} sm={6} md={4}>
                  <TextField label="Granulation" fullWidth size="small" value={form.granulation}
                    onChange={(e) => setForm((f: any) => ({ ...f, granulation: e.target.value }))}
                    helperText="e.g. pass, coarse, fine" />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Safety */}
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Safety Parameters</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Aflatoxin (ppb)"
                  type="number"
                  fullWidth
                  size="small"
                  value={form.aflatoxin}
                  onChange={(e) => setForm((f: any) => ({ ...f, aflatoxin: e.target.value }))}
                  inputProps={{ step: 0.1, min: 0 }}
                  error={form.aflatoxin !== '' && aflatoxinVal > 10}
                  helperText={form.aflatoxin !== '' && aflatoxinVal > 10 ? 'Exceeds 10 ppb limit' : 'Max 10 ppb'}
                  color={form.aflatoxin !== '' && aflatoxinVal > 10 ? 'error' : 'primary'}
                />
              </Grid>
              {numField('Foreign Matter (%)', 'foreignMatter', form, setForm, { step: 0.001, max: 5 })}
              {numField('Damaged Kernels (%)', 'damagedKernels', form, setForm, { max: 20 })}
            </Grid>
          </CardContent>
        </Card>

        {/* Notes */}
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <TextField label="Notes" fullWidth multiline rows={2} value={form.notes}
              onChange={(e) => setForm((f: any) => ({ ...f, notes: e.target.value }))} />
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="contained" type="submit" disabled={saving}>
            {saving ? 'Saving...' : isEdit ? 'Update Test' : 'Save Test'}
          </Button>
          <Button onClick={() => navigate('/milling/quality-tests')}>Cancel</Button>
        </Box>
      </form>
    </Box>
  );
};

export default QualityTestForm;
